// Who owns a team store. Grant (2026-11-02): every store has a named rep. On
// 11-03, 90 live stores had none on record and none of them had ever sold,
// while 28 of 63 v4 stores with a named rep had.
//
// Payouts, the payout email and the commission report read salesRepName /
// salesRepEmail. The v4 wizard used to write only repEmail, which none of those
// read, so a store can name a rep there and still be rep-less where it counts.
// GhostFit's own addresses (orders@, grantnoble@) are placeholders, not reps.

export type RepSource = 'salesRep' | 'repEmail';

export type StoreRep = { name: string; email: string; source: RepSource };

type RepFields = {
  salesRepName?: string | null;
  salesRepEmail?: string | null;
  repEmail?: string | null;
};

const PLACEHOLDER_DOMAINS = ['ghostfitsports.com', 'ghostfitapparel.com'];

export function isPlaceholderEmail(email?: string | null): boolean {
  const e = String(email || '').trim().toLowerCase();
  const at = e.lastIndexOf('@');
  if (at < 0) return false;
  return PLACEHOLDER_DOMAINS.includes(e.slice(at + 1));
}

function clean(v?: string | null): string {
  return String(v || '').trim();
}

// The rep payouts will see, else the rep only the wizard field names, else null.
export function storeRep(s: RepFields): StoreRep | null {
  const name = clean(s.salesRepName);
  const email = clean(s.salesRepEmail);
  const usableEmail = email && !isPlaceholderEmail(email) ? email : '';
  // A name alone counts: older stores and some LE reps carry a name with no
  // address, or with orders@ standing in for one. The payout line shows it.
  if (usableEmail || name) return { name, email: usableEmail, source: 'salesRep' };
  const wizard = clean(s.repEmail);
  if (wizard && !isPlaceholderEmail(wizard)) return { name: '', email: wizard, source: 'repEmail' };
  return null;
}

// True when payouts have no rep for the store: none at all, or one only in repEmail.
export function needsRep(s: RepFields): boolean {
  const rep = storeRep(s);
  return !rep || rep.source === 'repEmail';
}

export function repLabel(s: RepFields): string {
  const rep = storeRep(s);
  if (!rep) return 'No rep';
  if (rep.source === 'repEmail') return `${rep.email} (not on payouts)`;
  return rep.name || rep.email;
}
